import { ChangePasswordDto } from '../generated-types/changePasswordDto';
import { CreateAccountDto } from '../generated-types/createAccountDto';
import { ForgotPasswordDto } from '../generated-types/forgotPasswordDto';
import { ResetPasswordDto } from '../generated-types/resetPasswordDto';
import { UserLoginDto } from '../generated-types/userLoginDto';
import { AbstractApi } from './AbstractApi';

/**
 * An api class to interact with the auth controller
 */
export default class AuthApi extends AbstractApi {
  private static userFirstName = '';

  /**
   * Create a user account
   * @param createAccountDto The information to create the account with
   * @returns The code of the response and the token, if it was successful.
   */
  async CreateAccount(
    createAccountDto: CreateAccountDto
  ): Promise<{ code: number; token: string | undefined }> {
    const response = await fetch(this.BuildUrl('auth/create-account'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(createAccountDto),
    });

    // If the response is ok, get the token
    if (response.ok) {
      return {
        code: response.status,
        token: await response.text(),
      };
    }

    return {
      code: response.status,
      token: undefined,
    };
  }

  /**
   * Log a user in.
   * @param loginDto The dto that contains the login information
   * @returns The code of the response and the token, if it was successful.
   */
  async Login(
    loginDto: UserLoginDto
  ): Promise<{ code: number; token: string | undefined }> {
    const response = await fetch(this.BuildUrl('auth/login'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginDto),
    });

    // If the response is ok, get the token
    if (response.ok) {
      return {
        code: response.status,
        token: await response.text(),
      };
    }

    return {
      code: response.status,
      token: undefined,
    };
  }

  /**
   * Log the user out
   */
  async Logout(): Promise<void> {
    const response = await this.authFetch(this.BuildUrl('auth/logout'), {
      method: 'POST',
    });

    if (!response.ok) throw Error();
  }

  /**
   * Change the currently logged in user's password
   * @param changePasswordDto The information to change the users password
   * @returns The code of the response and the token, if it was successful. If a response 401, then it will include the body of the response.
   */
  async ChangePassword(
    changePasswordDto: ChangePasswordDto
  ): Promise<{ code: number; responseBody: string | undefined }> {
    const response = await this.authFetch(
      this.BuildUrl('auth/change-password'),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(changePasswordDto),
      }
    );

    // If the response is ok, get the new token
    // Or, if we get an unauthenticated response, interpret the body
    if (response.ok || response.status == 401) {
      return {
        code: response.status,
        responseBody: await response.text(),
      };
    }

    return {
      code: response.status,
      responseBody: undefined,
    };
  }

  /**
   * Get the first name of the currently authenticated user
   * @returns The first name of the logged in user
   */
  async GetUserFirstName(): Promise<string> {
    // See if we can grab the user's name from memory
    if (AuthApi.userFirstName == '') {
      const response = await this.authFetch(this.BuildUrl('auth/first-name'), {
        method: 'GET',
      });

      if (!response.ok) throw Error();

      AuthApi.userFirstName = await response.text();
    }

    return AuthApi.userFirstName;
  }

  /**
   * Handle a forgotten password
   * @param forgotPasswordDto The forgot password information
   */
  async ForgotPassword(forgotPasswordDto: ForgotPasswordDto): Promise<void> {
    const response = await this.authFetch(
      this.BuildUrl('auth/forgot-password'),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(forgotPasswordDto),
      }
    );

    if (!response.ok) throw Error();
  }

  /**
   * Reset the password
   * @param resetPassword The dto that contains the information to reset the password
   * @returns True, if the reset was successful. False, if the code has expired.
   */
  async ResetPassword(resetPassword: ResetPasswordDto): Promise<boolean> {
    const response = await this.authFetch(
      this.BuildUrl('auth/reset-password'),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resetPassword),
      }
    );

    if (!response.ok) {
      if (response.status == 400) return false;
      throw Error();
    }
    return true;
  }
}
